import { useCallback, useEffect, useState } from "react";

import { httpClient } from "@/lib/httpClient";

export interface NavPage {
  id: string;
  slug: string;
  title: string;
  /** Order among siblings: top-level pages in the section, children in their parent. */
  ordinal: number;
  /** The parent page; null or absent (an older server) at top level (navTree.ts). */
  parentPageId?: string | null;
  published: boolean;
  snippet: string | null;
}

export interface NavSection {
  id: string;
  slug: string;
  title: string;
  ordinal: number;
  restricted: boolean;
  pages: NavPage[];
}

export interface PageView {
  id: string;
  slug: string;
  ordinal: number;
  section: { id: string; slug: string; title: string; ordinal: number };
  title: string;
  /** The parent page for a child page, null at top level. */
  parent?: { slug: string; title: string; ordinal: number } | null;
  html: string;
  versionNo: number;
  publishedAt: string | null;
  approvedBy: string | null;
  sources: Array<{ id: string; name: string; webUrl: string }>;
  attachments: Array<{ id: string; filename: string; sizeBytes: number; mime: string }>;
  prev: { sectionSlug: string; slug: string; title: string } | null;
  next: { sectionSlug: string; slug: string; title: string } | null;
}

export const manualService = {
  getNav: () => httpClient.get<{ sections: NavSection[] }>("/api/cms/nav"),
  getPage: (sectionSlug: string, pageSlug: string) =>
    httpClient.get<PageView>(`/api/cms/pages/${sectionSlug}/${pageSlug}`),
};

/** "01"…"12" section code from the ordinal. */
export function sectionCode(ordinal: number): string {
  return String(ordinal).padStart(2, "0");
}

/** "OM-04.2" style document id from section + page ordinals; a child page
 * adds its own ordinal after its parent's: "OM-04.12.3". */
export function docCode(
  sectionOrdinal: number,
  pageOrdinal: number,
  childOrdinal?: number
): string {
  const code = `OM-${sectionCode(sectionOrdinal)}.${pageOrdinal}`;
  return childOrdinal === undefined ? code : `${code}.${childOrdinal}`;
}

// ---------------------------------------------------------------------------
// Shared nav cache: the sidebar and the manual pages all read the same tree;
// fetch it once per session and let mutating surfaces (editor) invalidate.
// ---------------------------------------------------------------------------

let navCache: NavSection[] | null = null;
let navPromise: Promise<NavSection[]> | null = null;
const listeners = new Set<() => void>();

async function loadNav(): Promise<NavSection[]> {
  navPromise ??= manualService.getNav().then((r) => {
    navCache = r.sections;
    return r.sections;
  });
  return navPromise;
}

/** Drop the cache and notify subscribers (call after publish/create). */
export function invalidateNav(): void {
  navCache = null;
  navPromise = null;
  for (const notify of listeners) notify();
}

export function useNav(): { sections: NavSection[] | null; error: boolean; reload: () => void } {
  const [sections, setSections] = useState<NavSection[] | null>(navCache);
  const [error, setError] = useState(false);

  const reload = useCallback(() => {
    invalidateNav();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      // Stale-while-revalidate: after invalidateNav() nulls the cache, keep the
      // sections we're already showing until the refetch lands. Downgrading to
      // a null cache here would collapse every consumer (the sidebar tree, and
      // with it any open access popover) to its loading state on each mutation.
      if (navCache) setSections(navCache);
      setError(false);
      void loadNav()
        .then((s) => {
          if (!cancelled) setSections(s);
        })
        .catch(() => {
          if (!cancelled) setError(true);
        });
    };
    listeners.add(refresh);
    if (navCache) setSections(navCache);
    else refresh();
    return () => {
      cancelled = true;
      listeners.delete(refresh);
    };
  }, []);

  return { sections, error, reload };
}
